// @flow
import URI from 'urijs';
import * as yup from 'yup';
import moment from 'moment';

import { getDomain } from '@clubmed/domains-service';

import {
  GM_NUMBER_PATTERN,
  PHONE_NUMBER_PATTERN,
} from 'Components/utils/common-validator/CommonValidators';
import { momentToApiDate, momentFromApiDate } from 'Utils/date/apiDate';
import { decryptHeptaSeller, isSeller } from 'Utils/seller/sellerUtils';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { CUSTOMER_BOOKING_PREBOOKING_FORM_SUBMIT } from 'Utils/request/requestDictionary';
import * as pleUtils from 'Utils/ple-utils';
import * as trackingApi from 'Tracking/tracking-api';
import * as routeBuilder from 'Routes/routeBuilder';

import {
  MAX_FORM_ADULTS,
  MAX_FORM_KIDS,
  MIN_FORM_ADULTS,
  MIN_FORM_KIDS,
} from './PrebookingForm.constants';

export type FormFieldOption = {
  name: string,
  value: string,
};

export type FormCivilities = {
  adults: Array<FormFieldOption>,
  kids: Array<FormFieldOption>,
};

export type FormCompanion = {
  birthdate: null | timestamp,
  civility: string,
  firstName: string,
  firstNameUnicode?: string,
  lastName: string,
  lastNameUnicode?: string,
};

export type FormTraveler = {
  birthdate: null | timestamp,
  civility: string,
  email: string,
  firstName: string,
  firstNameUnicode?: string,
  lastName: string,
  lastNameUnicode?: string,
  memberNumber: string,
  phone: string,
};

export type FormValues = {
  accommodation: string,
  accommodationCategory: string,
  adults: Array<FormCompanion>,
  adultsNumber: string,
  agency?: string,
  departureCity: string,
  departureDate: {
    from: null | Date,
    to: null,
  },
  duration: string,
  kids: Array<FormCompanion>,
  kidsNumber: string,
  optinEmail?: boolean,
  optinPhone?: boolean,
  optinSms?: boolean,
  productId: string,
  traveler: FormTraveler,
};

export type InitialFormData = {
  adults?: Array<FormCompanion>,
  adultsNumber?: string,
  kids?: Array<FormCompanion>,
  kidsNumber?: string,
  traveler: FormTraveler,
  optinEmail?: boolean,
  optinPhone?: boolean,
  optinSms?: boolean,
};

// Map Traveler in User Context

const getDateTimestamp = (birthdate): timestamp => {
  return moment(birthdate).valueOf();
};

export const getFormUser = (user: UserInfo): FormTraveler => {
  const {
    civility = '',
    dateOfBirth,
    email = '',
    firstName = '',
    GMNumber = '',
    lastName = '',
    mobilePhoneNumber = '',
    unicodeFirstName,
    unicodeLastName,
  } = user;
  return {
    civility,
    birthdate: dateOfBirth ? getDateTimestamp(dateOfBirth) : null,
    email,
    firstName,
    firstNameUnicode: unicodeFirstName,
    memberNumber: GMNumber,
    lastName,
    lastNameUnicode: unicodeLastName,
    phone: mobilePhoneNumber,
  };
};

type TravelerOptins = null | {
  optinEmail?: boolean,
  optinPhone?: boolean,
  optinSms?: boolean,
};

export const getFormOptins = (optins: TravelerOptins) => {
  const { optinEmail = false, optinPhone = false, optinSms = false } = optins || {};
  return {
    optinEmail,
    optinPhone,
    optinSms,
  };
};

// Map Traveler and Companions in CXO/Seller Context

type TravelersInput = {
  adults: Array<Adult>,
  kids: Array<Kid>,
};

const getFormTraveler = (traveler: null | Adult) => {
  const {
    birthdate,
    civility,
    email,
    firstName,
    lastName,
    gmNumber,
    mobile,
    unicodeFirstName,
    unicodeLastName,
  } = traveler || {};
  return {
    birthdate: birthdate || null,
    civility: civility || '',
    email: email || '',
    firstName: firstName || '',
    firstNameUnicode: unicodeFirstName || '',
    lastName: lastName || '',
    lastNameUnicode: unicodeLastName || '',
    memberNumber: gmNumber || '',
    phone: mobile || '',
  };
};

const getFormCompanion = (companion: Adult | Kid) => {
  return {
    birthdate: companion.birthdate || null,
    civility: typeof companion.civility !== 'string' ? '' : companion.civility,
    firstName: companion.firstName || '',
    lastName: companion.lastName || '',
  };
};

export const getFormTravelerAndCompanions = ({ adults, kids }: TravelersInput) => {
  const formTraveler: FormTraveler =
    adults.length > 0 ? getFormTraveler(adults[0]) : getFormTraveler(null);
  const adultCompanions = adults.slice(1);
  const formKids: Array<FormCompanion> = kids.map(getFormCompanion);
  const formAdults: Array<FormCompanion> = adultCompanions.map(getFormCompanion);
  return {
    traveler: formTraveler,
    adultsNumber: (formAdults.length + MIN_FORM_ADULTS).toString(),
    kidsNumber: formKids.length.toString(),
    adults: formAdults,
    kids: formKids,
    ...(adults.length > 0 ? getFormOptins(adults[0]) : getFormOptins(null)),
  };
};

// Initialize Form

export const getInitialFormValues = (agency: null | string): FormValues => {
  return {
    accommodation: '',
    accommodationCategory: '',
    adults: [],
    adultsNumber: MIN_FORM_ADULTS.toString(),
    agency: agency || '',
    departureCity: '',
    departureDate: { from: null, to: null },
    duration: '',
    kids: [],
    kidsNumber: MIN_FORM_KIDS.toString(),
    optinEmail: false,
    optinPhone: false,
    optinSms: false,
    productId: '',
    traveler: {
      birthdate: null,
      civility: '',
      email: '',
      firstName: '',
      lastName: '',
      memberNumber: '',
      phone: '',
    },
  };
};

// Set Form Schema

type FormSchemaParameters = {
  agencies: Array<string>,
  civilities: FormCivilities,
  locale: string,
  productIds: Array<string>,
};

// Birthdate from 1920
const minBirthdateTimestamp = moment('1920-01-01').valueOf();

// Birthdate less than 18 years old
const kidBirthdateTimestamp = moment()
  .subtract(18, 'years')
  .valueOf();

const adultBirthdateSchema = yup
  .number()
  .min(minBirthdateTimestamp)
  .max(kidBirthdateTimestamp)
  .nullable();

export const getAdultBirthdateValidator = (): yup => adultBirthdateSchema;

export const getKidFutureBithdateValidator = (): yup =>
  yup.number().test('', '', function(value: number) {
    const { context } = this.options;
    if (value && context?.values.departureDate.from) {
      const departureDate = moment(context?.values.departureDate.from).startOf('day');
      const kidBirthdate = moment(value).startOf('day');
      return kidBirthdate.isBefore(departureDate);
    }
    return false;
  });

export const getFormSchema = (
  t: string => string,
  { agencies, civilities, locale, productIds }: FormSchemaParameters,
) => {
  const agencySchema = yup
    .mixed()
    .oneOf(agencies)
    .required();
  const fistNameSchema = yup
    .string()
    .required(t('prebookingForm.mainTraveler.firstNamePlaceholder'))
    .max(20, t('prebookingForm.mainTraveler.firstNamePlaceholder'));
  const lastNameSchema = yup
    .string()
    .required(t('prebookingForm.mainTraveler.firstNamePlaceholder'))
    .max(30, t('prebookingForm.mainTraveler.firstNamePlaceholder'));
  const adultCivilities = civilities.adults.map(civility => civility.value);
  const kidCivilities = civilities.kids.map(civility => civility.value);
  const adultCompanionSchema = yup.object().shape({
    civility: yup
      .mixed()
      .oneOf(adultCivilities)
      .required(),
    birthdate: adultBirthdateSchema,
    firstName: fistNameSchema,
    lastName: lastNameSchema,
  });
  const kidCompanionSchema = yup.object().shape({
    civility: yup.mixed().oneOf(kidCivilities),
    birthdate: yup
      .number()
      .min(kidBirthdateTimestamp)
      .nullable()
      .required(),
    firstName: fistNameSchema,
    lastName: lastNameSchema,
  });

  const travelerSchema = yup.object().shape({
    birthdate: adultBirthdateSchema,
    civility: yup
      .mixed()
      .oneOf(adultCivilities)
      .required(),
    email: yup
      .string()
      .email(t('prebookingForm.mainTraveler.emailPlaceholder'))
      .required(t('prebookingForm.mainTraveler.emailPlaceholder')),
    firstName: fistNameSchema,
    lastName: lastNameSchema,
    memberNumber: yup.string().matches(GM_NUMBER_PATTERN),
    phone: yup
      .string()
      .required(t('prebookingForm.mainTraveler.phonePlaceholder'))
      .matches(getDomain(locale)?.fields?.phoneNumber || PHONE_NUMBER_PATTERN, {
        excludeEmptyString: true,
        message: t('prebookingForm.mainTraveler.phonePlaceholder'),
      }),
  });

  return yup.object().shape({
    accommodation: yup.string(),
    accommodationCategory: yup.string().required(),
    adults: yup
      .array()
      .min(MIN_FORM_ADULTS - 1)
      .max(MAX_FORM_ADULTS - 1)
      .of(adultCompanionSchema),
    adultsNumber: yup.string().required(),
    ...(agencies.length ? { agency: agencySchema } : null),
    departureCity: yup.string().required(),
    departureDate: yup
      .object()
      .shape({
        from: yup.date(),
        to: yup.date().nullable(),
      })
      .required(),
    duration: yup
      .string()
      .required()
      .matches(/^[0-9]{1,2}$/), // 1 or 2 digits
    kids: yup
      .array()
      .min(MIN_FORM_KIDS)
      .max(MAX_FORM_KIDS)
      .of(kidCompanionSchema),
    kidsNumber: yup.string().required(),
    optinEmail: yup.boolean(),
    optinPhone: yup.boolean(),
    optinSms: yup.boolean(),
    productId: yup
      .mixed()
      .oneOf(productIds)
      .required(),
    traveler: travelerSchema,
  });
};

// Submit prebooking form

type FormSubmitParameters = {
  actions: {
    setSubmitting: boolean => void,
  },
  appContext: AppContext,
  cxoId: null | string,
  history: RouterHistory,
  location: RouterLocation<{}, {}>,
  redirects: {
    errorUrl: string,
    successUrl: string,
  },
};

const formatDate = (date: timestamp | Date) => momentToApiDate(moment(date));

const getCompanionQuery = companion => {
  const {
    birthdate,
    civility,
    firstName,
    firstNameUnicode = '',
    lastName,
    lastNameUnicode = '',
  } = companion;
  return `{
    ${birthdate ? `birthdate: "${formatDate(birthdate)}"` : ''}
    civility: "${civility}"
    lastName: "${lastName}"
    unicodeLastName: "${lastNameUnicode}"
    firstName: "${firstName}"
    unicodeFirstName: "${firstNameUnicode}"
  }`;
};

const getCompanionsQuery = companions => {
  return companions.reduce((acc, companion) => {
    return `${acc}${getCompanionQuery(companion)}`;
  }, '');
};

export const getPrebookingFormQuery = (
  values: FormValues,
  cxoId: null | string,
  appContext: AppContext,
) => {
  const {
    accommodation,
    accommodationCategory,
    adults,
    agency,
    departureCity,
    departureDate,
    duration,
    kids,
    optinEmail,
    optinPhone,
    optinSms,
    productId,
    traveler,
  } = values;
  const {
    birthdate,
    civility,
    email,
    firstName,
    firstNameUnicode = '',
    lastName,
    lastNameUnicode = '',
    memberNumber = '',
    phone,
  } = traveler;
  return `mutation {
    prebooking(
      productId: "${productId}"
      departureCity: "${departureCity}"
      departureDate: "${formatDate(departureDate.from || Date.now())}"
      duration: ${parseInt(duration, 10)}
      accommodation: ${accommodation ? `"${accommodation}"` : 'null'}
      accommodationCategory: "${accommodationCategory}"
      agency: ${agency ? `"${agency}"` : 'null'}
      ${isSeller(appContext) && cxoId ? `agentId : "${decryptHeptaSeller(cxoId)}"` : ''}
      mainContact: {
        gmNumber: "${memberNumber}"
        civility: "${civility}"
        lastName: "${lastName}"
        unicodeLastName: "${lastNameUnicode}"
        firstName: "${firstName}"
        unicodeFirstName: "${firstNameUnicode}"
        ${birthdate ? `birthdate: "${formatDate(birthdate)}"` : ''}
        email: "${email}"
        mobile: "${phone}"
        ${typeof optinSms !== 'undefined' ? `optinSms: ${optinSms.toString()}` : ''}
        ${typeof optinPhone !== 'undefined' ? `optinPhone: ${optinPhone.toString()}` : ''}
        ${typeof optinEmail !== 'undefined' ? `optinEmail: ${optinEmail.toString()}` : ''}
      }
      adults: [${getCompanionsQuery(adults)}]
      children: [${getCompanionsQuery(kids)}]
    ) {
      errors {
        errorKey
        errorCode
      }
      neolaneID
    }
  }`;
};

type FormResponse = {
  prebooking: {
    neolaneID: null | string,
    errors: Array<{
      errorCode: string,
      errorKey: string,
    }>,
  },
};

export const handleSubmit = async (values: FormValues, params: FormSubmitParameters) => {
  const { actions, appContext, cxoId, history, location, redirects } = params;
  const { errorUrl, successUrl } = redirects;
  const prebookingFormQuery = getPrebookingFormQuery(values, cxoId, appContext);

  try {
    // Submit data
    const { prebooking }: FormResponse = await graphQLClient.runNoCacheQuery(
      prebookingFormQuery,
      appContext,
      {
        requestName: CUSTOMER_BOOKING_PREBOOKING_FORM_SUBMIT,
      },
    );

    // Set isLoading to false
    actions.setSubmitting(false);

    // Track
    const neolaneId = prebooking.neolaneID;
    if (neolaneId) {
      trackingApi.setDataLayer(
        {
          categoryGA: 'preBookingForm',
          actionGA: 'CTASendRequest',
          labelGA: values.productId,
          user: { neolaneID: neolaneId },
        },
        appContext,
      );
    }

    // Redirect
    const requestHasSucceeded = prebooking.errors.length === 0;
    let redirectUri = requestHasSucceeded ? successUrl : errorUrl;
    if (isSeller(appContext) && requestHasSucceeded) {
      redirectUri = routeBuilder.buildUrl('prebookingformSuccessSeller');
    }
    history.push(
      new URI(redirectUri)
        .query({ ...location.queryParams, trackingPageLoadAppendSuffix: values.productId })
        .toString(),
    );
  } catch (error) {
    if (pleUtils.hasAuthResponseError(error)) {
      pleUtils.redirectOnLoginPage({
        locale: appContext.locale,
        redirectPath: location.pathname,
        // state: values,
      });
    } else {
      history.push(
        new URI(errorUrl)
          .query({ ...location.queryParams, trackingPageLoadAppendSuffix: values.productId })
          .toString(),
      );
    }
  }
};

export const getClosingDatesRanges = (
  openingDatesRanges: Array<{ opening: string, closing: string }>,
) =>
  // $FlowFixMe
  openingDatesRanges
    .sort((date1, date2) => Number(date1.opening) - Number(date2.opening))
    // $FlowFixMe
    .map((openingDate, index, openingDates) => ({
      after: momentFromApiDate(openingDate.closing).toDate(),
      before: momentFromApiDate(
        openingDates[index + 1]?.opening || openingDates[0].opening,
      ).toDate(),
    }));
